import { ReactNode, useState } from 'react';
import { useMutation } from '@apollo/client';
import moment from 'moment';
import { Alert, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import {
  AdminEditSongpopLegacyPackInput,
  AdminEditSongpopLegacyPackMutation,
  AdminEditSongpopLegacyPackMutationVariables,
  AdminSongpopLegacyPackStatusFragmentFragment,
  SongpopLegacyPackGrantSource,
} from '../../../../../../__gqltypes__';
import { ADMIN_EDIT_SONGPOP_LEGACY_PACK } from '../../../graphql';

type Status = AdminSongpopLegacyPackStatusFragmentFragment;
type Drop = { id: string; name: string; skuId: string };
type FieldKey = Exclude<keyof AdminEditSongpopLegacyPackInput, 'userId'>;

type Props = {
  status: Status;
  drops: Drop[];
  onSaved: (status: Status) => void;
  // Grant / revoke running: the edit buttons wait.
  disabled?: boolean;
};

type Field = {
  key: FieldKey;
  label: string;
  kind: 'bool' | 'select' | 'text' | 'date' | 'int';
  // NOT NULL column: cannot be emptied.
  required?: boolean;
  hint?: string;
};

const FIELDS: Field[] = [
  {
    key: 'flag',
    label: 'Flag (AppUser.hasLegacyPack)',
    kind: 'bool',
    hint: 'Active / not active. The pass follows: off revokes it (revokedAt = now), on reactivates it. A player without a pass needs a Grant first.',
  },
  { key: 'grantSource', label: 'Source', kind: 'select', required: true },
  {
    key: 'dropId',
    label: 'Sale config',
    kind: 'select',
    hint: "Decides the owner's exclusive rewards and daily chest.",
  },
  { key: 'store', label: 'Store', kind: 'text' },
  { key: 'sku', label: 'SKU', kind: 'text' },
  { key: 'purchasedAt', label: 'Granted at', kind: 'date', required: true },
  { key: 'transactionId', label: 'Transaction', kind: 'text' },
  { key: 'grantReason', label: 'Grant reason', kind: 'text' },
  { key: 'grantedBy', label: 'Granted by', kind: 'text' },
  { key: 'revokedAt', label: 'Revoked at', kind: 'date', hint: 'Empty = not revoked.' },
  { key: 'revokeReason', label: 'Revoke reason', kind: 'text' },
  { key: 'revokedBy', label: 'Revoked by', kind: 'text' },
  { key: 'lastChestClaim', label: 'Last chest claim', kind: 'date', hint: "Empty = today's chest can be opened." },
  {
    key: 'lastDailyDiamondsClaim',
    label: 'Last daily diamonds claim',
    kind: 'date',
    hint: "Empty = today's Maestro diamonds can be claimed.",
  },
  { key: 'liveShowFreeUsedThisWeek', label: 'Free Live Show used this week', kind: 'int' },
  { key: 'liveShowWeekStart', label: 'Live Show week started', kind: 'date', hint: 'Empty = no week running.' },
  {
    key: 'foundersWallHiddenByAdmin',
    label: 'Hidden from Founders wall (admin)',
    kind: 'bool',
    hint: 'Moderation (offensive name...): off the wall and no founder badge for other players. The player cannot undo it.',
  },
  {
    key: 'foundersWallHidden',
    label: 'Hidden from Founders wall (player)',
    kind: 'bool',
    hint: "The player's own switch in the app.",
  },
];

const formatDate = (date?: string | null) => (date ? moment(date).format('YYYY-MM-DD HH:mm') : '-');
// <input type="datetime-local"> works in the browser's local time, like the dates shown.
const toLocalInput = (date?: string | null) => (date ? moment(date).format('YYYY-MM-DDTHH:mm') : '');

// Editor value of a field: a string for every input ('' = empty), a boolean for the flag.
function draftFromStatus(field: Field, status: Status): string | boolean {
  const value = status[field.key as keyof Status];
  if (field.kind === 'bool') {
    return !!value;
  }
  if (field.kind === 'date') {
    return toLocalInput(value as string | null);
  }
  if (field.kind === 'int') {
    return String(value ?? 0);
  }
  return (value as string | null) ?? '';
}

// The input value of one field: '' = empty (null).
function inputValue(field: Field, draft: string | boolean) {
  if (field.kind === 'bool') {
    return draft as boolean;
  }
  const text = (draft as string).trim();
  if (!text) {
    return null;
  }
  if (field.kind === 'date') {
    return moment(text).toDate();
  }
  if (field.kind === 'int') {
    return Number(text);
  }
  return text;
}

/**
 * The pass of one player as stored, every field with its own edit button (every environment,
 * prod included). The flag and the pass row move together (the server keeps them in sync);
 * Grant / Revoke stay the way to change ownership with the rewards.
 */
export function LegacyPackEditForm({ status, drops, onSaved, disabled }: Props) {
  const [editing, setEditing] = useState<FieldKey | null>(null);
  const [draft, setDraft] = useState<string | boolean>('');
  const [confirmFlag, setConfirmFlag] = useState(false);

  const [edit, { loading: saving, error }] = useMutation<
    AdminEditSongpopLegacyPackMutation,
    AdminEditSongpopLegacyPackMutationVariables
  >(ADMIN_EDIT_SONGPOP_LEGACY_PACK, {
    onCompleted: (result) => {
      onSaved(result.adminEditSongpopLegacyPack);
      setEditing(null);
    },
  });

  const editedField = FIELDS.find((field) => field.key === editing) ?? null;
  const unchanged = !!editedField && draft === draftFromStatus(editedField, status);
  const emptyingRequired = !!editedField?.required && inputValue(editedField, draft) === null;

  const startEdit = (field: Field) => {
    setEditing(field.key);
    setDraft(draftFromStatus(field, status));
  };

  const save = () => {
    setConfirmFlag(false);
    if (!editedField) {
      return;
    }
    edit({
      variables: { input: { userId: status.userId, [editedField.key]: inputValue(editedField, draft) } },
    }).catch(() => {
      // surfaced through `error`
    });
  };

  const display = (field: Field): ReactNode => {
    const value = status[field.key as keyof Status];
    switch (field.kind) {
      case 'bool':
        return value ? 'on' : 'off';
      case 'date':
        return formatDate(value as string | null);
      case 'int':
        return String(value ?? 0);
      default:
        if (field.key === 'dropId' && value) {
          const drop = drops.find((candidate) => candidate.id === value);
          return drop ? `${drop.name} - ${drop.skuId}` : String(value);
        }
        return (value as string | null) ?? '-';
    }
  };

  const editor = (field: Field): ReactNode => {
    switch (field.kind) {
      case 'bool':
        return (
          <Form.Check
            type="switch"
            id={`legacy-edit-${field.key}-${status.userId}`}
            label={draft ? 'on' : 'off'}
            checked={draft as boolean}
            onChange={(e) => setDraft(e.target.checked)}
          />
        );
      case 'select':
        return (
          <Form.Control size="sm" as="select" value={draft as string} onChange={(e) => setDraft(e.target.value)}>
            {field.key === 'grantSource' ? (
              Object.values(SongpopLegacyPackGrantSource).map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))
            ) : (
              <>
                <option value="">None (no exclusive rewards, no chest)</option>
                {draft && !drops.some((drop) => drop.id === draft) && (
                  <option value={draft as string}>{draft} (unknown)</option>
                )}
                {drops.map((drop) => (
                  <option key={drop.id} value={drop.id}>
                    {drop.name} - {drop.skuId}
                  </option>
                ))}
              </>
            )}
          </Form.Control>
        );
      case 'date':
        return (
          <div className="d-flex align-items-center">
            <Form.Control
              size="sm"
              type="datetime-local"
              value={draft as string}
              onChange={(e) => setDraft(e.target.value)}
            />
            {!field.required && draft && (
              <Button size="sm" variant="link" onClick={() => setDraft('')}>
                Clear
              </Button>
            )}
          </div>
        );
      case 'int':
        return (
          <Form.Control
            size="sm"
            type="number"
            min={0}
            value={draft as string}
            onChange={(e) => setDraft(e.target.value)}
          />
        );
      default:
        return <Form.Control size="sm" value={draft as string} onChange={(e) => setDraft(e.target.value)} />;
    }
  };

  return (
    <>
      {error && <Alert variant="danger">{error.message}</Alert>}
      <p className="mb-2">
        Founder number: <strong>{status.founderNumber ? `#${status.founderNumber}` : '-'}</strong>{' '}
        <small className="text-muted">(given once after the grant, kept for good, not editable)</small>
      </p>
      <Table size="sm" className="mb-3">
        <tbody>
          {FIELDS.map((field) => {
            const isEditing = editing === field.key;
            return (
              <tr key={field.key}>
                <td className="align-middle">{field.label}</td>
                <td className="align-middle text-break">
                  {isEditing ? (
                    <>
                      {editor(field)}
                      {field.hint && <small className="text-muted">{field.hint}</small>}
                    </>
                  ) : (
                    display(field)
                  )}
                </td>
                <td className="align-middle text-right text-nowrap" style={{ width: 150 }}>
                  {isEditing ? (
                    <>
                      <Button
                        size="sm"
                        variant="primary"
                        className="mr-1"
                        disabled={saving || unchanged || emptyingRequired}
                        title={emptyingRequired ? 'This field cannot be empty' : ''}
                        onClick={() => (field.key === 'flag' ? setConfirmFlag(true) : save())}
                      >
                        {saving ? <Spinner animation="border" size="sm" /> : 'Save'}
                      </Button>
                      <Button size="sm" variant="secondary" disabled={saving} onClick={() => setEditing(null)}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline-primary"
                      title={`Edit ${field.label}`}
                      disabled={disabled || saving || editing !== null}
                      onClick={() => startEdit(field)}
                    >
                      <i className="fas fa-pen" />
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      <Modal show={confirmFlag} onHide={() => setConfirmFlag(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Turn the flag {draft ? 'on' : 'off'}?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {draft
            ? 'Turns the perks and the Maestro leaderboard on, and reactivates the pass (revokedAt cleared). The player needs a pass: grant one first otherwise.'
            : 'Turns the perks and the Maestro leaderboard off, and revokes the pass (revokedAt = now, reason "admin edit: status off"). It stays off until turned on again or a new grant.'}{' '}
          No rewards are given or taken: use Grant / Revoke for that. Beta and prod share the database.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmFlag(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
